import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; 
import emailjs from 'emailjs-com';
import * as XLSX from 'xlsx';

// הגדרת מותגים וספקים
const BRANDS = [
  {
    key: 'visa',
    label: 'ויזה',
    providers: ['ויזה כאל (גמא)', 'לאומי קארד (max)', 'ישראכרט'],
  },
  {
    key: 'mastercard',
    label: 'מאסטרקארד',
    providers: ['ויזה כאל (גמא)', 'לאומי קארד (max)', 'ישראכרט'],
  },
  {
    key: 'isracard',
    label: 'ישראכרט',
    providers: ['ויזה כאל (גמא)', 'לאומי קארד (max)', 'ישראכרט'],
  },
  {
    key: 'amex',
    label: 'אמריקן אקספרס',
    providers: ['ישראכרט'],
  },
  {
    key: 'diners',
    label: 'דיינרס',
    providers: ['ויזה כאל (גמא)'],
  },
];

// אייקונים לכל מותג
const BRAND_ICONS: Record<string, React.ReactNode> = {
  visa: <img src="https://upload.wikimedia.org/wikipedia/commons/4/41/Visa_Logo.png" alt="Visa" className="h-7 w-7 inline align-middle mr-2 bg-white rounded" loading="lazy" />,
  mastercard: <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-7 w-7 inline align-middle mr-2 bg-white rounded" loading="lazy" />,
  isracard: <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRlmbDAFuWhrO398GPGV8Z6FMXgcrjs1VXatQ&s" alt="Isracard" className="h-7 w-7 inline align-middle mr-2 bg-white rounded" loading="lazy" />,
  amex: <img src="https://he.americanexpress.co.il/globalassets/CardsImages/91_105_-1/hotnewlogo.jpg" alt="Amex" className="h-7 w-7 inline align-middle mr-2 bg-white rounded" loading="lazy" />,
  diners: <img src="https://www.diners-store.co.il/img/assets/logo_diners_new.svg" alt="Diners" style={{ height: '32px', width: 'auto', minWidth: '32px', marginRight: '0.5rem', background: '#fff', borderRadius: '6px', display: 'inline-block', verticalAlign: 'middle' }} loading="lazy" />,
};

const SERVICE_ID = 'service_xuvopc8'; // עדכן לפי הצורך
const TEMPLATE_ID = 'template_dl34urm'; // עדכן לפי הצורך
const USER_ID = 'a5OPti5YKYKk2ZKIM'; // עדכן לפי הצורך

const saveAs = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, 0);
};

// שמור בקשה שנשלחה ב-localStorage (היסטוריית בקשות)
function saveTerminalRequest(request: any) {
  const raw = localStorage.getItem('terminalRequests');
  let requests: any[] = [];
  if (raw) {
    try {
      requests = JSON.parse(raw);
    } catch {}
  }
  requests.push(request);
  localStorage.setItem('terminalRequests', JSON.stringify(requests));
}

export default function TerminalForm({ onNewRequest, selectedTerminalModel }: { onNewRequest?: (request: any) => void, selectedTerminalModel?: string }) {
  const [form, setForm] = useState({
    businessName: '',
    regType: '',
    regId: '',
    street: '',
    streetNumber: '',
    city: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    shippingFullAddress: '',
    shippingContactName: '',
    shippingContactPhone: '',
    terminalModel: selectedTerminalModel || '',
    terminalsQty: 1,
    brands: [],
    providers: {}, // { visa: "ישראכרט", ... }
    providerNumbers: {}, // { "ישראכרט": "123456", ... }
  });
  const [submitted, setSubmitted] = useState(false);
  const [requestStatus, setRequestStatus] = useState<'pending' | 'in_progress' | 'done'>('pending');
  const [showRequestDetails, setShowRequestDetails] = useState(false);
  const navigate = useNavigate();

  // ניווט בטוח: מונע יציאה אם לא נשלח
  const safeNavigate = (to: string) => {
    if (!submitted) {
      alert('כבר ביצעת תשלום. על מנת שנוכל להמשיך לטפל בבקשה שלך, אנא מלא ושלח את הטופס.');
      return;
    }
    navigate(to);
  };

  // מניעת עזיבת הטופס לפני שליחה
  useEffect(() => {
    const message =
      'כבר ביצעת תשלום. על מנת שנוכל להמשיך לטפל בבקשה שלך, אנא מלא ושלח את הטופס.';

    // התראה לפני סגירת/רענון הדף
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!submitted) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    // מניעת חזור בדפדפן
    const handlePopState = (e: PopStateEvent) => {
      if (!submitted) {
        alert(message);
        window.history.pushState(null, '', window.location.href);
      }
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [submitted]);

  // שמירת טיוטה ב-localStorage בכל שינוי, רק אם לא נשלח
  useEffect(() => {
    if (!submitted) {
      localStorage.setItem('terminalFormDraft', JSON.stringify(form));
    }
  }, [form, submitted]);

  // שינוי: טען טיוטה לעריכה אם לא נשלח, ואם נשלח טען את הבקשה האחרונה שנשלחה (לצפייה בלבד)
  useEffect(() => {
    if (!submitted) {
      // עריכה: טען טיוטה
      const draft = localStorage.getItem('terminalFormDraft');
      if (draft) {
        try {
          setForm(JSON.parse(draft));
        } catch {}
      }
    } else {
      // צפייה בלבד: טען את הבקשה האחרונה שנשלחה
      const requestsRaw = localStorage.getItem('terminalRequests');
      if (requestsRaw) {
        try {
          const requests = JSON.parse(requestsRaw);
          if (Array.isArray(requests) && requests.length > 0) {
            setForm(requests[requests.length - 1]);
          }
        } catch {}
      }
    }
    // שינוי: הוסף תלות ריקה כדי שירוץ רק פעם אחת על mount
  }, [submitted]);

  // אחרי שליחה, מחק טיוטה
  useEffect(() => {
    if (submitted) {
      localStorage.removeItem('terminalFormDraft');
    }
  }, [submitted]);

  // הוסף useEffect שמעדכן את דגם המסוף אם הפרופס משתנה
  useEffect(() => {
    if (selectedTerminalModel) {
      setForm((prev) => ({
        ...prev,
        terminalModel: selectedTerminalModel,
      }));
    }
  }, [selectedTerminalModel]);

  // שלוף דגם מסוף מה-localStorage אם קיים
	useEffect(() => {
		const localSelected = localStorage.getItem('selectedTerminalModel');
		if (localSelected) {
			setForm((prev) => ({
				...prev,
				terminalModel: localSelected,
			}));
		}
	}, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type, checked } = e.target;
    if (name === 'brands') {
      const brand = value;
      setForm((prev) => {
        const brands = prev.brands.includes(brand)
          ? prev.brands.filter((b: string) => b !== brand)
          : [...prev.brands, brand];
        return { ...prev, brands };
      });
    } else if (name.startsWith('provider-')) {
      const brand = name.replace('provider-', '');
      setForm((prev) => ({
        ...prev,
        providers: { ...prev.providers, [brand]: value },
      }));
    } else if (name.startsWith('providerNumber-')) {
      const brand = name.replace('providerNumber-', '');
      // שמור לפי שם הספק שנבחר, לא לפי המותג
      const supplier = form.providers[brand] || '';
      if (supplier) {
        setForm((prev) => ({
          ...prev,
          providerNumbers: { ...prev.providerNumbers, [supplier]: value },
        }));
      }
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  // הוסף משתנה לבחירת הסביבה
  const WEBHOOK_URL_TEST = 'https://ofekperetz.app.n8n.cloud/webhook-test/06ebebce-00db-45de-bd0a-2357563517d5';
  const WEBHOOK_URL_PROD = 'https://ofekperetz.app.n8n.cloud/webhook/06ebebce-00db-45de-bd0a-2357563517d5';

  // בחר את ה-URL לפי הסביבה (אפשר לשנות ל-production לפי הצורך)
  const isProduction = true; // שנה ל-true בפרודקשן
  const WEBHOOK_URL = isProduction ? WEBHOOK_URL_PROD : WEBHOOK_URL_TEST;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // בניית טבלה דו-עמודתית ("שדה", "ערך")
    const rows2col: string[][] = [
      ['שדה','ערך'],
      ['שם בית עסק', form.businessName],
      ['צורת ההתאגדות', form.regType],
      ['ח"פ / ע.מ', form.regId],
      ['רחוב', `${form.street} ${form.streetNumber}`],
      ['עיר', form.city],
      ['שם איש קשר', form.contactName],
      ['טלפון נייד', form.contactPhone],
      ['אימייל איש קשר', form.contactEmail],
      ['כתובת מלאה למשלוח', form.shippingFullAddress],
      ['איש קשר למשלוח', form.shippingContactName],
      ['טלפון נייד למשלוח', form.shippingContactPhone],
      ['דגם מסוף', form.terminalModel],
      ['כמות מסופים', String(form.terminalsQty)],
    ];
    // בניית אובייקט מותגים עם ספק ומספר ספק לפי הספק שנבחר
    const brandsDetails = form.brands.map((brand: string, idx: number) => {
      const supplier = form.providers[brand] || '';
      const supplierNumber = supplier ? form.providerNumbers[supplier] || '' : '';
      return {
        brand,
        supplier,
        supplierNumber,
      };
    });
    // עדכון rows2col להצגה
    brandsDetails.forEach((detail, idx) => {
      const label = (BRANDS.find(b => b.key === detail.brand)?.label) || detail.brand;
      rows2col.push(
        [`מותג ${idx+1}`, label],
        [`ספק ${idx+1}`, detail.supplier],
        [`מספר ספק ${idx+1}`, detail.supplierNumber]
      );
    });

    // שלח את כל נתוני הטופס ל-webhook כ-JSON
    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          brandsDetails, // שלח את המידע החדש
        }),
      });
      // בדוק אם יש שגיאת רשת (CORS, דומיין, אינטרנט)
      if (!response.ok) {
        throw new Error(`Webhook returned status ${response.status}`);
      }
      setSubmitted(true);
      setRequestStatus('pending');
      if (onNewRequest) onNewRequest({ ...form, status: 'pending', id: Date.now() });
      saveTerminalRequest({ ...form, status: 'pending', id: Date.now() });
    } catch (err) {
      alert('שליחת הבקשה נכשלה. שגיאת "Failed to fetch" מצביעה על בעיית רשת או CORS. ודא שהשרת שלך מאפשר קריאות מהדפדפן (CORS) ושכתובת ה-webhook נכונה.');

    }
  };

  return (
    <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl p-8 mt-8">
      {!submitted ? (
        <form onSubmit={handleSubmit} className="space-y-4 flex flex-col">
          <div className="text-lg font-bold mb-4 text-indigo-700">
            טופס חיבור מסוף
          </div>
          <div className="bg-yellow-100 border border-yellow-300 rounded-xl p-4 mb-4 text-yellow-900 font-semibold text-center">
            שים לב! כבר ביצעת תשלום עבור השירות.<br />
            על מנת שנוכל להמשיך לטפל בבקשה שלך, אנא מלא ושלח את הטופס.
          </div>
          <div>
            <label className="block font-medium mb-1 text-gray-700">שם בית עסק</label>
            <input
              type="text"
              name="businessName"
              value={form.businessName}
              onChange={handleChange}
              className="w-full border border-indigo-200 rounded-xl p-3 focus:ring-2 focus:ring-indigo-400 transition bg-gray-50"
              required
              placeholder="לדוג' מסעדת גן עדן"
            />
          </div>
          <div>
            <label className="block font-medium mb-1 text-gray-700">צורת ההתאגדות</label>
            <select
              name="regType"
              value={form.regType}
              onChange={handleChange}
              className="w-full border border-indigo-200 rounded-xl p-3 focus:ring-2 focus:ring-indigo-400 transition bg-gray-50"
              required
            >
              <option value="">בחר...</option>
              <option value="חברה בע״מ">חברה בע״מ</option>
              <option value="עוסק מורשה">עוסק מורשה</option>
              <option value="עמותה">עמותה</option>
              <option value="אחר">אחר</option>
            </select>
          </div>
          <div>
            <label className="block font-medium mb-1 text-gray-700">ח&quot;פ / ע.מ</label>
            <input
              type="text"
              name="regId"
              value={form.regId}
              onChange={handleChange}
              className="w-full border border-indigo-200 rounded-xl p-3 focus:ring-2 focus:ring-indigo-400 transition bg-gray-50"
              required
              placeholder='ח"פ / ע.מ'
            />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block font-medium mb-1 text-gray-700">רחוב</label>
              <input
                type="text"
                name="street"
                value={form.street}
                onChange={handleChange}
                className="w-full border border-indigo-200 rounded-xl p-3 focus:ring-2 focus:ring-indigo-400 transition bg-gray-50"
                required
              />
            </div>
            <div className="w-24">
              <label className="block font-medium mb-1 text-gray-700">מספר</label>
              <input
                type="text"
                name="streetNumber"
                value={form.streetNumber}
                onChange={handleChange}
                className="w-full border border-indigo-200 rounded-xl p-3 focus:ring-2 focus:ring-indigo-400 transition bg-gray-50"
                required
              />
            </div>
          </div>
          <div>
            <label className="block font-medium mb-1 text-gray-700">עיר</label>
            <input
              type="text"
              name="city"
              value={form.city}
              onChange={handleChange}
              className="w-full border border-indigo-200 rounded-xl p-3 focus:ring-2 focus:ring-indigo-400 transition bg-gray-50"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1 text-gray-700">שם איש קשר</label>
            <input
              type="text"
              name="contactName"
              value={form.contactName}
              onChange={handleChange}
              className="w-full border border-indigo-200 rounded-xl p-3 focus:ring-2 focus:ring-indigo-400 transition bg-gray-50"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1 text-gray-700">טלפון נייד</label>
            <input
              type="text"
              name="contactPhone"
              value={form.contactPhone}
              onChange={handleChange}
              className="w-full border border-indigo-200 rounded-xl p-3 focus:ring-2 focus:ring-indigo-400 transition bg-gray-50"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1 text-gray-700">אימייל איש קשר</label>
            <input
              type="email"
              name="contactEmail"
              value={form.contactEmail}
              onChange={handleChange}
              className="w-full border border-indigo-200 rounded-xl p-3 focus:ring-2 focus:ring-indigo-400 transition bg-gray-50"
              required
              placeholder="your@email.com"
            />
          </div>
          <div>
            <label className="block font-medium mb-1 text-gray-700">כתובת מלאה למשלוח</label>
            <input
              type="text"
              name="shippingFullAddress"
              value={form.shippingFullAddress}
              onChange={handleChange}
              className="w-full border border-indigo-200 rounded-xl p-3 focus:ring-2 focus:ring-indigo-400 transition bg-gray-50"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1 text-gray-700">איש קשר למשלוח</label>
            <input
              type="text"
              name="shippingContactName"
              value={form.shippingContactName}
              onChange={handleChange}
              className="w-full border border-indigo-200 rounded-xl p-3 focus:ring-2 focus:ring-indigo-400 transition bg-gray-50"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1 text-gray-700">טלפון נייד למשלוח</label>
            <input
              type="text"
              name="shippingContactPhone"
              value={form.shippingContactPhone}
              onChange={handleChange}
              className="w-full border border-indigo-200 rounded-xl p-3 focus:ring-2 focus:ring-indigo-400 transition bg-gray-50"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1 text-gray-700">דגם מסוף</label>
            <input
              type="text"
              name="terminalModel"
              value={form.terminalModel}
              readOnly
              className="w-full border border-indigo-200 rounded-xl p-3 bg-gray-100"
              placeholder="לדוג' N6"
            />
          </div>
          <div>
            <label className="block font-medium mb-1 text-gray-700">כמות מסופים</label>
            <input
              type="number"
              name="terminalsQty"
              value={form.terminalsQty}
              min={1}
              onChange={handleChange}
              className="w-full border border-indigo-200 rounded-xl p-3 focus:ring-2 focus:ring-indigo-400 transition bg-gray-50"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1 text-gray-700">מותגים לסליקה</label>
            <ul className="space-y-2">
              {BRANDS.map((b) => (
                <li key={b.key}>
                  <label
                    className="flex items-center gap-3 border rounded-xl px-4 py-3 cursor-pointer bg-gray-50 hover:bg-indigo-50 transition shadow-sm"
                    style={{ minHeight: 56 }}
                  >
                    <input
                      type="checkbox"
                      name="brands"
                      value={b.key}
                      checked={form.brands.includes(b.key)}
                      onChange={handleChange}
                      className="accent-indigo-600 scale-125"
                    />
                    <span className="flex items-center gap-3">
                      <span className="flex items-center justify-center h-8 w-8 bg-white rounded shadow">
                        {BRAND_ICONS[b.key]}
                      </span>
                      <span className="font-bold text-gray-700 text-base">{b.label}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
          {form.brands.map((brand: string) => {
            const provider = form.providers[brand] || '';
            // בדוק אם אותו ספק נבחר במותג אחר
            const isProviderShared = form.brands.some(
              (otherBrand: string) =>
                otherBrand !== brand && form.providers[otherBrand] === provider
            );
            // אם יש מספר ספק משותף, שלוף אותו
            let sharedProviderNumber = '';
            if (provider && form.providerNumbers[provider]) {
              sharedProviderNumber = form.providerNumbers[provider];
            }
            // אם הספק לא משותף, אפשר לערוך
            const isReadOnly = !!sharedProviderNumber && isProviderShared;

            return (
              <div key={brand} className="mt-2">
                <label className="block font-medium mb-1 text-gray-700">
                  ספק סליקה עבור {BRAND_ICONS[brand]} {BRANDS.find(b => b.key === brand)?.label}
                </label>
                <select
                  name={`provider-${brand}`}
                  value={provider}
                  onChange={handleChange}
                  className="w-full border border-indigo-200 rounded-xl p-3 focus:ring-2 focus:ring-indigo-400 transition bg-gray-50"
                  required
                >
                  <option value="">בחר ספק...</option>
                  {BRANDS.find(b => b.key === brand)?.providers.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <label className="block font-medium mb-1 text-gray-700 mt-2">
                  מספר ספק עבור {provider || BRANDS.find(b => b.key === brand)?.label}
                </label>
                <input
                  type="text"
                  name={`providerNumber-${brand}`}
                  value={sharedProviderNumber}
                  onChange={handleChange}
                  className={`w-full border border-indigo-200 rounded-xl p-3 ${isReadOnly ? 'bg-gray-100 text-gray-500' : 'focus:ring-2 focus:ring-indigo-400 transition bg-gray-50'}`}
                  required
                  readOnly={isReadOnly}
                  placeholder="הזן מספר ספק"
                />
              </div>
            );
          })}
          <div className="flex gap-2 justify-end mt-8">
            <button
              type="submit"
              className="px-4 py-2 rounded-xl font-bold text-white bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 transition"
            >
              שלח בקשה
            </button>
          </div>
        </form>
      ) : (
        <div className="text-center">
          <div className="text-2xl font-bold mb-4">
            הבקשה נשלחה בהצלחה!
          </div>
          <div className="bg-green-100 text-green-800 rounded-xl p-6 shadow mb-6">
            סטטוס הבקשה: <span className="font-bold">{requestStatus === 'pending' ? 'ממתין לטיפול' : requestStatus === 'in_progress' ? 'בתהליך' : 'הושלם'}</span>
            <br />
            ניצור איתך קשר בקרוב להמשך תהליך החיבור.
          </div>
          <button
            className="px-4 py-2 rounded-xl font-bold bg-indigo-600 text-white"
            onClick={() => setShowRequestDetails(true)}
          >
            צפייה בפרטי הבקשה
          </button>
          {showRequestDetails && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full">
                <div className="text-xl font-bold mb-4 text-indigo-700">פרטי הבקשה</div>
                <div className="space-y-2 text-gray-800 text-right">
                  <div><span className="font-bold">שם בית עסק:</span> {form.businessName}</div>
                  <div><span className="font-bold">איש קשר:</span> {form.contactName} ({form.contactPhone})</div>
                  <div><span className="font-bold">אימייל:</span> {form.contactEmail}</div>
                  <div><span className="font-bold">כתובת:</span> {form.street} {form.streetNumber}, {form.city}</div>
                  <div><span className="font-bold">דגם מסוף:</span> {form.terminalModel}</div>
                  <div><span className="font-bold">כמות מסופים:</span> {form.terminalsQty}</div>
                  <div><span className="font-bold">מותגים:</span> {(form.brands || []).join(', ')}</div>
                  {/* אפשר להוסיף עוד שדות לפי הצורך */}
                </div>
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    className="px-4 py-2 rounded-xl font-bold bg-gray-200"
                    onClick={() => setShowRequestDetails(false)}
                  >
                    סגור
                  </button>
                  <button
                    className="px-4 py-2 rounded-xl font-bold bg-indigo-600 text-white"
                    onClick={() => {
                      setRequestStatus('in_progress');
                      setShowRequestDetails(false);
                      // כאן אפשר להפעיל המשך תהליך (למשל מעבר לשלב הבא)
                    }}
                  >
                    המשך תהליך
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}